import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import { ISupplier } from '@bridebook/models/source/models/Suppliers.types';
import { ISupplierAdmin } from '@bridebook/models/source/models/Suppliers/Admin.types';
import { Row } from '@bridebook/ui';
import { IconCamera, IconVideo, IconVideoTour } from '@bridebook/ui/src/icons';
import { GallerySection, openGallery } from '@bridebook/web/lib/supplier/supplier-gallery';
import MediaButton from './media-button/media-button';
import { Fragment } from 'react';

interface IProps {
  videosCount: number;
  premium?: ISupplierAdmin['premium'] | null;
  photosCount: number;
  videoToursCount: number;
  supplier: ISupplier;
  isLoggedIn: boolean;
}

const MediaButtonSection = ({
  premium,
  photosCount,
  videosCount,
  videoToursCount,
  supplier,
  isLoggedIn,
}: IProps) => {
  const router = useRouter();
  const { t } = useTranslation('supplier');
  const hasPhotos = photosCount > 0;
  const hasVideos = videosCount > 0;
  const hasVideoTours = videoToursCount > 0 && premium;
  const isVideographer = supplier.type[0] === 'video';
  const isMusician = supplier.type[0] === 'music';

  const onOpenGallery = (section?: GallerySection) => {
    if (supplier) {
      openGallery(router, supplier, section);
    }
  };

  const videoButtonText = isMusician
    ? t('supplierMedia.button.samples', {
        count: videosCount,
      })
    : t('supplierMedia.button.video', {
        count: videosCount,
      });

  const videoButtonIcon = isMusician ? '🎵' : <IconVideo color="space" width={20} />;

  const buttons = {
    photo: hasPhotos && (
      <MediaButton
        icon={<IconCamera color="space" width={21} />}
        text={t('supplierMediaButtonPhoto', {
          count: photosCount,
        })}
        onClick={onOpenGallery}
        isLoggedIn={isLoggedIn}
      />
    ),
    video: hasVideos && (
      <MediaButton
        icon={videoButtonIcon}
        text={videoButtonText}
        onClick={() => onOpenGallery(GallerySection.Videos)}
        isLoggedIn={isLoggedIn}
      />
    ),
    videoTour: hasVideoTours && (
      <MediaButton
        icon={<IconVideoTour color="space" width={24} />}
        text={t('supplierMedia.button.virtualTour', {
          count: videoToursCount,
        })}
        onClick={() => onOpenGallery(GallerySection.VideoTours)}
        isLoggedIn={isLoggedIn}
      />
    ),
  };

  const orderedButtons =
    isVideographer || isMusician
      ? [buttons.video, buttons.photo, buttons.videoTour]
      : [buttons.photo, buttons.video, buttons.videoTour];

  return (
    <Row data-name="media-button-section" columnGap={[0, 3]}>
      {orderedButtons.map((button, index) => (
        <Fragment key={index}>{button}</Fragment>
      ))}
    </Row>
  );
};

export default MediaButtonSection;
